import { Injectable } from '@angular/core';
import { StorageService, StorageKey } from '../services/storage.service';

/**
 * OF-2F — Cross-origin session handoff (cloud HTTPS → hub HTTP).
 *
 * Packs the current POS + admin authentication and active-store
 * context into a URL fragment so the operator can be redirected to
 * the Local Hub Mode URL without re-logging-in. The hub-origin app
 * consumes the fragment on boot, hydrates its own localStorage,
 * clears the fragment from the URL, then renders normally.
 *
 * Why URL fragment (not query string): fragments are NEVER sent to
 * the server. They stay client-side, never appear in access logs,
 * never travel through API Gateway. This is the standard pattern for
 * OAuth implicit-flow callbacks.
 *
 * Security:
 *   • Payload carries a TTL (2 minutes) so a shared/bookmarked URL
 *     cannot be replayed hours later.
 *   • Tokens inside are already server-side-expiring (POS 4h,
 *     StoreEdgeAgent 24h) — the TTL here is an extra belt.
 *   • Nothing sensitive leaks: the tokens are the same JWTs
 *     localStorage would hold anyway on the hub origin after PIN login.
 */
export interface HandoffPayload {
  /** Schema version. Bump if shape changes. */
  v: 1;
  /** Issued timestamp in UTC. Consumed rejects payloads older than 2 minutes. */
  issuedAtUtc: string;
  /**
   * OF-3C — Once-only nonce. Consumed nonces are recorded in
   * sessionStorage to block replay (bookmark, browser-history back,
   * link-sharing within TTL). Without this, a user could unintentionally
   * re-hydrate stale auth if they revisited the handoff URL.
   */
  nonce?: string;
  posToken?: string;
  posAuth?: unknown;             // opaque — shape owned by PosStateService
  activeStore?: unknown;         // opaque — shape owned by StoreContextService
  adminToken?: string;
  adminAuth?: unknown;
  /**
   * OF-FIX-RTC-1 — the standalone POS store code (`pos_standalone_code`).
   *
   * Without it, a handoff that arrives with an expired POS token falls
   * through `posAuthGuard` to the BARE `/pos` PIN screen instead of
   * `/pos/s/<code>`, and the terminal has no store context at all —
   * which is where the "No store selected" message on the return trip
   * came from.
   */
  posStandaloneCode?: string;
  /**
   * OF-FIX-RTC-1 — the Edge Client hardware device id.
   *
   * `DeviceIdentityService` caches this in localStorage, which is
   * PER-ORIGIN. Hub origin and cloud origin therefore hold separate
   * copies, and a full-page hop between them lands on an origin that
   * may never have seen the id — after which the HTTP interceptor sends
   * the `web` sentinel and the backend attributes every write and every
   * print job to the store's sentinel device instead of this terminal.
   *
   * The handoff already carries POS token, POS auth and active store;
   * device identity is the one piece of per-origin state it forgot.
   */
  edgeClientId?: string;
}

@Injectable({ providedIn: 'root' })
export class HubHandoffService {
  private readonly MAX_HANDOFF_AGE_MS = 2 * 60 * 1000;
  /** OF-3C — sessionStorage key for tracking consumed handoff nonces. */
  private readonly CONSUMED_NONCES_KEY = 'ipos_consumed_handoff_nonces';

  constructor(private storage: StorageService) { }

  /**
   * Build the redirect URL. Returns null if the hub URL is missing or
   * malformed. Never throws — the banner component can rely on a null
   * return to disable the "Switch" button.
   */
  buildRedirectUrl(hubUrl: string | null | undefined): string | null {
    const base = (hubUrl ?? '').trim().replace(/\/+$/, '');
    if (!base) return null;
    try { new URL(base); } catch { return null; }

    const payload: HandoffPayload = {
      v: 1,
      issuedAtUtc: new Date().toISOString(),
      nonce: this.newNonce(),
      posToken: this.storage.getRaw(StorageKey.POS_TOKEN) ?? undefined,
      posAuth: this.storage.get(StorageKey.POS_AUTH) ?? undefined,
      activeStore: this.storage.get(StorageKey.ACTIVE_STORE) ?? undefined,
      adminToken: this.storage.getRaw(StorageKey.TOKEN) ?? undefined,
      adminAuth: this.storage.get(StorageKey.AUTH) ?? undefined,
      posStandaloneCode:
        this.storage.getRaw(StorageKey.POS_STANDALONE_CODE) ?? undefined,
      edgeClientId: this.readEdgeClientId(),
    };
    const encoded = this.encode(payload);
    return `${base}/#handoff=${encoded}`;
  }

  /**
   * On boot, check if the current URL carries a handoff fragment. If
   * yes: validate, hydrate localStorage, clear the fragment, return
   * true. Returns false when no handoff is present OR the handoff is
   * malformed / stale.
   */
  consumeFromUrl(): boolean {
    if (typeof window === 'undefined') return false;
    const hash = window.location.hash ?? '';
    if (!hash.startsWith('#handoff=')) return false;

    const encoded = hash.substring('#handoff='.length);
    let payload: HandoffPayload | null = null;
    try {
      payload = this.decode(encoded);
    } catch {
      this.clearFragment();
      return false;
    }
    if (!payload || payload.v !== 1 || !payload.issuedAtUtc) {
      this.clearFragment();
      return false;
    }

    const ageMs = Date.now() - new Date(payload.issuedAtUtc).getTime();
    if (isNaN(ageMs) || ageMs < 0 || ageMs > this.MAX_HANDOFF_AGE_MS) {
      // eslint-disable-next-line no-console
      console.warn('[OF-2F handoff] Ignoring stale handoff payload (age ms):', ageMs);
      this.clearFragment();
      return false;
    }

    // OF-3C — Once-only nonce check. Blocks replay of the same handoff
    // URL from bookmarks, shared links, or browser-history navigation
    // within the TTL window. Older payloads without a nonce are accepted
    // for backward compatibility (belt + braces: the TTL still caps them).
    if (payload.nonce) {
      if (this.isNonceConsumed(payload.nonce)) {
        // eslint-disable-next-line no-console
        console.warn('[OF-3C handoff] Rejecting replayed handoff nonce:', payload.nonce);
        this.clearFragment();
        return false;
      }
      this.markNonceConsumed(payload.nonce);
    }

    try {
      if (payload.posToken) this.storage.setRaw(StorageKey.POS_TOKEN, payload.posToken);
      if (payload.posAuth) this.storage.set(StorageKey.POS_AUTH, payload.posAuth);
      if (payload.activeStore) this.storage.set(StorageKey.ACTIVE_STORE, payload.activeStore);
      if (payload.adminToken) this.storage.setRaw(StorageKey.TOKEN, payload.adminToken);
      if (payload.adminAuth) this.storage.set(StorageKey.AUTH, payload.adminAuth);
      if (payload.posStandaloneCode) {
        this.storage.setRaw(StorageKey.POS_STANDALONE_CODE, payload.posStandaloneCode);
      }
      // Written to the RAW key DeviceIdentityService reads (it is not a
      // StorageKey — the service owns an unprefixed `edgeClientId`), so
      // the interceptor stops falling back to the `web` sentinel on the
      // destination origin.
      if (payload.edgeClientId) {
        try { localStorage.setItem('edgeClientId', payload.edgeClientId); }
        catch { /* quota / unavailable — interceptor falls back as before */ }
      }
    } finally {
      this.clearFragment();
    }
    return true;
  }

  // ── internals ───────────────────────────────────────────────

  /**
   * Reads the durable Edge Client id without taking a dependency on
   * DeviceIdentityService (which would be a circular import through the
   * interceptor chain). Mirrors that service's cache precedence:
   * localStorage first, then the legacy registration sessionStorage.
   * Returns undefined for a plain browser session so the payload stays
   * clean rather than carrying the `web` sentinel across.
   */
  private readEdgeClientId(): string | undefined {
    try {
      const ls = localStorage.getItem('edgeClientId');
      if (ls && ls.trim() && ls !== 'web') return ls.trim();
    } catch { /* unavailable */ }
    try {
      const ss = sessionStorage.getItem('edgeClientDeviceId');
      if (ss && ss.trim() && ss !== 'web') return ss.trim();
    } catch { /* unavailable */ }
    return undefined;
  }

  private encode(p: HandoffPayload): string {
    const json = JSON.stringify(p);
    // base64url (no +/= for URL safety)
    const b64 = this.toBase64(json);
    return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  private decode(encoded: string): HandoffPayload {
    const b64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    const json = this.fromBase64(padded);
    return JSON.parse(json) as HandoffPayload;
  }

  private toBase64(s: string): string {
    // Use TextEncoder path where available (handles UTF-8 cleanly).
    if (typeof TextEncoder !== 'undefined' && typeof btoa === 'function') {
      const bytes = new TextEncoder().encode(s);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
      return btoa(binary);
    }
    // Fallback (older browsers).
    return btoa(unescape(encodeURIComponent(s)));
  }

  private fromBase64(s: string): string {
    if (typeof TextDecoder !== 'undefined' && typeof atob === 'function') {
      const binary = atob(s);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      return new TextDecoder().decode(bytes);
    }
    return decodeURIComponent(escape(atob(s)));
  }

  // OF-3C — Nonce helpers. sessionStorage bounds the list to the tab
  // session, which is fine: the whole handoff flow only matters within
  // a 2-min window of an active tab. Cap size to 32 entries to prevent
  // memory growth if someone scripts handoff URLs.

  private newNonce(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return `n-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  private readConsumedNonces(): string[] {
    try {
      if (typeof sessionStorage === 'undefined') return [];
      const raw = sessionStorage.getItem(this.CONSUMED_NONCES_KEY);
      if (!raw) return [];
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr.filter(x => typeof x === 'string') : [];
    } catch { return []; }
  }

  private isNonceConsumed(nonce: string): boolean {
    return this.readConsumedNonces().includes(nonce);
  }

  private markNonceConsumed(nonce: string): void {
    try {
      if (typeof sessionStorage === 'undefined') return;
      const list = this.readConsumedNonces();
      list.push(nonce);
      // Keep last 32 only.
      const trimmed = list.length > 32 ? list.slice(-32) : list;
      sessionStorage.setItem(this.CONSUMED_NONCES_KEY, JSON.stringify(trimmed));
    } catch { /* best-effort */ }
  }

  private clearFragment(): void {
    try {
      history.replaceState(null, '', window.location.pathname + window.location.search);
    } catch {
      /* History API unavailable — fragment stays but does no harm */
    }
  }
}
